/**
 * Display-only formatting/parsing helpers for the design system (U5).
 *
 * PRODUCT LAW: these are presentation utilities ONLY. All money math
 * (totals, markup/margin, tax) is derived by the shared engine in
 * `@buildledger/shared` (U3) — never recompute business totals here.
 */

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** `1234.5` → `$1,234.50`. Null/undefined → em dash. */
export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return usd.format(value);
}

/** Plain grouped number. `trim` drops trailing fraction zeros (qty display). */
export function formatNumber(
  value: number | null | undefined,
  options: { decimals?: number; trim?: boolean } = {},
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  const decimals = options.decimals ?? 2;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: options.trim ? 0 : decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Parse user-typed numeric text. Accepts `$`, thousands separators, spaces.
 * Returns `null` for empty input and `NaN` for unparseable input — callers
 * decide whether NaN means "revert" (inputs do).
 */
export function parseNumeric(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, '');
  if (cleaned === '' || cleaned === '-') return null;
  return Number(cleaned);
}

export function clampNumber(value: number, min?: number, max?: number): number {
  let v = value;
  if (min !== undefined && v < min) v = min;
  if (max !== undefined && v > max) v = max;
  return v;
}

/** Round to `decimals` places (display rounding only — see file banner). */
export function roundTo(value: number, decimals: number): number {
  const f = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * f) / f;
}
